import * as React from "react";
import { Button, Card, Divider } from "antd";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CaretRightOutlined } from "@ant-design/icons";
import { CardContent } from "../../components/antd/card/CardContent";
import { homeLink, request } from "../../services/cpln";
import { CardCollapsibleContent } from "../../components/antd/card/CardCollapsibleContent";
import { GroupInput } from "../../schema/types/group/input";
import { v4 as uuidv4 } from "uuid";
import { inputToQuery, QueryMatchOptions } from "../../schema/types/query/input";
import { getUserTableItem, UserTableItem } from "../../mst/kinds/user.helpers";
import { Table } from "../../newcomponents/table/table";
import { UserNameDescriptionColumn } from "../../newcomponents/table/columns/wellKnown/userNameDescriptionColumn";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { formSetValue, useCpFormValue } from "../gvc/testNewForm/utils";
import { store } from "../../store";
import { CpFormItem } from "../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../gvc/testNewForm/CpLabel";
import { CpSwitch } from "../gvc/testNewForm/CpSwitch";
import { CpSelect } from "../gvc/testNewForm/CpSelect";
import { CpErrorWarning } from "../gvc/testNewForm/CpErrorWarning";
import { CpArrayObject } from "../gvc/testNewForm/CpArrayObject";
import { CpArrayItemQueryTerm } from "../policy/targetQuery/CpArrayItemQueryTerm";
import { CpErrorWarningArrayIcon } from "../gvc/testNewForm/CpErrorWarningArrayIcon";

interface Props {}
export const UserQuery: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const query = useCpFormValue(`userQuery`);
  const queryResult = useCpFormValue(`meta.queryResult.user.data`);
  const queryResultIsCollapsed = useCpFormValue(`meta.queryResult.user.isCollapsed`);

  const [isEvaluatingQuery, setIsEvaluatingQuery] = React.useState(false);
  async function evaluateQuery() {
    try {
      setIsEvaluatingQuery(true);
      const form = store.getState().form.instances[id];
      const values: GroupInput = form.values as any;

      const queryApi = inputToQuery(values.userQuery, "user");
      const { data } = await request({
        method: "post",
        url: `${homeLink("user")}/-query`,
        body: queryApi,
      });
      formSetValue(id, `meta.queryResult.user.data`, data);
      setIsEvaluatingQuery(false);
    } catch (e) {
      formSetValue(id, `meta.queryResult.user.data`, null);
      setIsEvaluatingQuery(false);
    }
  }

  const userTableItems = (queryResult?.items || []).map((i) => getUserTableItem({ item: i }));

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            <div className={`flex items-center`}>
              <div>User Query</div>
              <div style={{ transform: "translateY(0px)" }}>
                <CpErrorWarningArrayIcon names={["userQuery"]} />
              </div>
            </div>
          </CardTitle>
          <CardContent>
            <CpFormItem>
              <div className="flex items-center gap-2">
                <CpLabel name={`userQuery.isEnabled`}>Configure User Query</CpLabel>
                <CpSwitch name={`userQuery.isEnabled`} />
              </div>
            </CpFormItem>
            <CardCollapsibleContent collapsed={!query.isEnabled}>
              <CpFormItem>
                <CpLabel name={`userQuery.match`}>Match</CpLabel>
                <CpSelect name={`userQuery.match`} options={QueryMatchOptions} style={{ width: 250 }} />
                <CpErrorWarning name={`userQuery.match`} />
              </CpFormItem>
              <CpArrayObject
                name={`userQuery.terms`}
                labels={[
                  { label: "Type", span: 1 },
                  { label: "Key", span: 2 },
                  { label: "Op", span: 1 },
                  { label: "Value", span: 2 },
                ]}
                label={`Terms`}
                factoryFn={() => ({ id: uuidv4(), key: "", termType: "tag", op: "=", value: "" })}
              >
                <CpArrayItemQueryTerm kind={`user`} />
              </CpArrayObject>
              <Divider />
              <CardTitle style={queryResultIsCollapsed ? { marginBottom: 0 } : undefined}>
                <div className={`flex items-baseline ${queryResultIsCollapsed ? "" : "mb-4"}`}>
                  <div
                    className={`flex items-baseline gap-2 color-link-hover cursor-pointer`}
                    onClick={() => {
                      formSetValue(id, `meta.queryResult.user.isCollapsed`, !queryResultIsCollapsed);
                    }}
                  >
                    <CaretRightOutlined rotate={queryResultIsCollapsed ? 0 : 90} className={`self-center`} />
                    <span>Query Evaluation</span>
                  </div>
                  <div className="flex-grow" />
                </div>
              </CardTitle>
              <CardCollapsibleContent collapsed={queryResultIsCollapsed}>
                <Button
                  htmlType={`button`}
                  block
                  type={"default"}
                  onClick={() => evaluateQuery()}
                  disabled={isEvaluatingQuery}
                  loading={isEvaluatingQuery}
                >
                  Evaluate Query
                </Button>
                {queryResult ? (
                  <Table<UserTableItem>
                    tableId="group-user-query-result"
                    data={userTableItems}
                    hideSettings
                    columns={[
                      UserNameDescriptionColumn({ enableSort: false }, { shouldForceTargetBlank: () => true }), //
                      TagsColumn(),
                      CreatedColumn(),
                      LastModifiedColumn(),
                    ]}
                  />
                ) : null}
              </CardCollapsibleContent>
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
